import { FilesetResolver, ImageEmbedder } from '@mediapipe/tasks-vision';
import type { DetectionBox } from './Detector';
import type { DeepFeature } from './ReIdFeatures';

export interface DeepReIdOptions {
  wasmBaseUrl: string;
  modelAssetPath: string;
}

export interface DeepReIdExtractorLike {
  initialize(): Promise<void>;
  isReady(): boolean;
  extract(video: HTMLVideoElement, box: DetectionBox): Promise<DeepFeature | null>;
  dispose(): void;
}

const INPUT_SIZE = 192;

export class DeepReIdExtractor implements DeepReIdExtractorLike {
  private embedder: ImageEmbedder | null = null;
  private readonly cropCanvas: HTMLCanvasElement;

  constructor(private readonly opts: DeepReIdOptions) {
    this.cropCanvas = document.createElement('canvas');
    this.cropCanvas.width = INPUT_SIZE;
    this.cropCanvas.height = INPUT_SIZE;
  }

  async initialize(): Promise<void> {
    if (this.embedder) return;
    const fileset = await FilesetResolver.forVisionTasks(this.opts.wasmBaseUrl);
    this.embedder = await ImageEmbedder.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: this.opts.modelAssetPath,
        delegate: 'GPU',
      },
      runningMode: 'IMAGE',
      l2Normalize: true,
      quantize: false,
    });
  }

  isReady(): boolean {
    return this.embedder !== null;
  }

  async extract(video: HTMLVideoElement, box: DetectionBox): Promise<DeepFeature | null> {
    if (!this.embedder) return null;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w <= 0 || h <= 0) return null;
    const sx = Math.max(0, box.x * w);
    const sy = Math.max(0, box.y * h);
    const sw = Math.max(1, Math.min(w - sx, box.w * w));
    const sh = Math.max(1, Math.min(h - sy, box.h * h));
    const ctx = this.cropCanvas.getContext('2d');
    if (!ctx) return null;
    try {
      ctx.clearRect(0, 0, INPUT_SIZE, INPUT_SIZE);
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, INPUT_SIZE, INPUT_SIZE);
      const result = this.embedder.embed(this.cropCanvas);
      const first = result.embeddings[0];
      if (!first) return null;
      const raw = first.floatEmbedding;
      if (!raw || raw.length === 0) return null;
      return {
        kind: 'deep',
        embedding: raw instanceof Float32Array ? raw : new Float32Array(raw),
        sampleCount: 1,
      };
    } catch (err) {
      console.error('[DeepReIdExtractor] extract failed', err);
      return null;
    }
  }

  dispose(): void {
    this.embedder?.close();
    this.embedder = null;
  }
}
